import { NullableDateInput, stringifyDate } from '@superdispatch/dates';
import { CUSTOMER_TYPES } from '@superdispatch/sdk';
import { z } from 'zod';

// `SHIPPER_RECOMMENDED` has a special badge
export const SHIPPER_RECOMMENDED_ALGORITHM = 'SHIPPER_RECOMMENDED';
const LOAD_SUGGESTION_ALGORITHMS = [
  'DRIVING_AREA_BASED',
  'GEO_CLUSTER_BASED',
  'LOADS_ALONG_THE_ROUTE',
  'SLB_RECENT_SEARCHES_BASED',
  'BACKHAUL',
  'SHIPPER_PREVIOUSLY_DISPATCHED_LOADS_BASED',
  SHIPPER_RECOMMENDED_ALGORITHM,
] as const;
export type LoadSuggestionAlgorithm =
  (typeof LOAD_SUGGESTION_ALGORITHMS)[number];

export const loadSuggestionAlgorithmsDescriptionMap: Record<
  Exclude<LoadSuggestionAlgorithm, typeof SHIPPER_RECOMMENDED_ALGORITHM>,
  string
> = {
  DRIVING_AREA_BASED: 'Near Driver’s Delivery',
  GEO_CLUSTER_BASED: 'Near Driver’s Routes',
  LOADS_ALONG_THE_ROUTE: 'Along Route',
  SLB_RECENT_SEARCHES_BASED: 'Recent Search Match',
  BACKHAUL: 'Backhaul',
  SHIPPER_PREVIOUSLY_DISPATCHED_LOADS_BASED: 'Preferred Shipper',
};

function toJodaISO(value: unknown): null | string {
  return (
    stringifyDate(value as NullableDateInput, { format: 'JodaISO' }) ?? null
  );
}

// dimensions arrive as strings, sometimes comma-grouped (`"3,500"`)
function parseDimension(value: unknown): null | number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  const parsed = Number.parseFloat(value.replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
}

export type StepDTO = z.infer<typeof stepSchema>;
const stepSchema = z.object({
  venue: z.object({
    city: z.string().nullish(),
    state: z.string().nullish(),
    zip: z.string().nullish(),
    business_type: z.enum(CUSTOMER_TYPES).nullish().catch(null),
  }),
  date_type: z
    .enum(['estimated', 'exact', 'not_earlier_than', 'not_later_than'])
    .nullable()
    .catch(null),
  scheduled_at: z.preprocess(toJodaISO, z.string().nullable()),
  scheduled_ends_at: z.preprocess(toJodaISO, z.string().nullable()),
});

export type LoadSuggestionDTO = z.infer<typeof loadSuggestionSchema>;
const loadSuggestionSchema = z.object({
  guid: z.string(),
  number: z.string().nullable(),
  price: z.number().nullable(),
  price_per_mile: z.number().nullable(),
  is_posted_to_private_loadboard: z
    .boolean()
    .nullish()
    .transform((value) => value ?? false),
  is_seen: z
    .boolean()
    .nullish()
    .transform((value) => value ?? false),
  carrier_access: z
    .object({
      can_book: z
        .boolean()
        .nullish()
        .transform((value) => value ?? false),
    })
    .nullish()
    .transform((value) => value ?? { can_book: false }),
  suggestion_guid: z.string(),
  suggestion_id: z.number(),
  algorithms: z
    .array(z.enum(LOAD_SUGGESTION_ALGORITHMS).nullable().catch(null))
    .default([]),
  posting_guid: z.string().nullable(), // It could be nullable for old postings
  pickup: stepSchema,
  delivery: stepSchema,
  distance_miles: z.number().nullable(),
  created_at: z.preprocess(toJodaISO, z.string().nullable()),
  posted_to_loadboard_at: z.preprocess(toJodaISO, z.string().nullable()),
  transport_type: z
    .enum(['OPEN', 'ENCLOSED', 'DRIVEAWAY'])
    .nullable()
    .catch(null),
  payment: z.object({
    terms: z
      .string()
      .nullish()
      .transform((value) => value ?? undefined),
    method: z
      .string()
      .nullish()
      .transform((value) => value ?? undefined),
  }),
  shipper: z.object({
    name: z.string().nullable(),
    rating_details: z
      .object({
        total_rating_count: z.number().nullable(),
        overall_rating: z.number().nullable(),
      })
      .nullable(),
  }),
  vehicles: z
    .array(
      z.object({
        guid: z.string(),
        is_inoperable: z
          .boolean()
          .nullish()
          .transform((value) => value ?? false),
        make: z.string().nullable(),
        model: z.string().nullable(),
        type: z.string().nullable(),
        year: z.number().nullable(),
        curb_weight: z.preprocess(parseDimension, z.number().nullable()),
        curb_weight_unit: z.string().nullable(),
        height: z.preprocess(parseDimension, z.number().nullable()),
        length: z.preprocess(parseDimension, z.number().nullable()),
        width: z.preprocess(parseDimension, z.number().nullable()),
      }),
    )
    .default([]),

  /* This field describes how suggested load was generated
    - If it is empty then it is generated by Dispatcher
    - If it is not empty then it is generated by Driver
   */
  driver_names: z.array(z.string()).default([]),
});

export const loadSuggestionListSchema = z.array(loadSuggestionSchema);
